import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import testAffiliatesService, {
  TestAffiliateAccount,
} from "../../../services/test-affiliates.service";
import PageHeader from "../../../components/common/PageHeader";
import { Eye, EyeOff, Copy, CheckCircle, Info } from "react-feather";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";

function TestAccountCard({
  account,
  index,
}: {
  account: TestAffiliateAccount;
  index: number;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const copyToClipboard = async (
    text: string,
    type: "email" | "password"
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "email") {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      }
      toast.success(`${type === "email" ? "Email" : "Password"} copied!`);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Test Affiliate {index + 1}
        </h3>
        <span className="px-3 py-1 text-xs font-medium text-brand-700 bg-brand-100 rounded-full">
          Test Account
        </span>
      </div>

      <div className="space-y-4">
        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Email
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="text"
              value={account.email}
              readOnly
              className="flex-1"
            />
            <Button
              size="icon"
              onClick={() => copyToClipboard(account.email, "email")}
              title="Copy email"
            >
              {copiedEmail ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Password
          </label>
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={account.password}
                readOnly
                className="pr-10"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <Button
              size="icon"
              onClick={() => copyToClipboard(account.password, "password")}
              title="Copy password"
            >
              {copiedPassword ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="bg-card rounded-lg shadow-sm border border-border p-6 animate-pulse"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-muted rounded w-40"></div>
            <div className="h-6 bg-muted rounded w-24"></div>
          </div>
          <div className="space-y-4">
            <div>
              <div className="h-4 bg-muted rounded w-16 mb-2"></div>
              <div className="h-10 bg-muted rounded"></div>
            </div>
            <div>
              <div className="h-4 bg-muted rounded w-20 mb-2"></div>
              <div className="h-10 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TestAccounts() {
  const {
    data: testAccounts,
    isLoading,
    error,
  } = useQuery<TestAffiliateAccount[]>({
    queryKey: ["testAccounts"],
    queryFn: testAffiliatesService.getTestAccounts,
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Test Accounts" />

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-blue-900 mb-1">
            About Test Accounts
          </h4>
          <p className="text-sm text-blue-800">
            Use these credentials to test the affiliate experience. These
            accounts can search for your brand, join programs, and test all
            affiliate features. When these test affiliates search for brands,
            your brand will appear at the top of their search results.
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && <LoadingSkeleton />}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-sm text-red-800">
            Failed to load test accounts. Please try again later.
          </p>
        </div>
      )}

      {/* Test Accounts */}
      {testAccounts && testAccounts.length > 0 && (
        <div className="space-y-6">
          {testAccounts.map((account, index) => (
            <TestAccountCard
              key={account.affiliateId}
              account={account}
              index={index}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {testAccounts && testAccounts.length === 0 && (
        <EmptyState icon={<Info />} title="No test accounts available" />
      )}
    </div>
  );
}
